import { CancelInviteModalProps } from '../types';
import { FC, useEffect } from 'react';
import { Icon } from 'features/common/components/Icon';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { useIframeDialogClamp } from 'features/common/hooks/useIframeDialogClamp';
import { useTranslation } from 'react-i18next';
import styles from './PunchupCancelInviteModal.module.css';

export const PunchupCancelInviteModal: FC<CancelInviteModalProps> = ({ modalInfo, showModal, closeModal }) => {
	const { t } = useTranslation();
	// Clamp to the visible mobile viewport when embedded, so the card fits the fold and its buttons stay
	// reachable (TIC-636). `'fixed'` — this overlay renders inline (not portalled), so it must anchor to
	// the iframe viewport, not a positioned ancestor.
	const { overlayStyle, containerStyle } = useIframeDialogClamp(showModal, 'fixed');

	useEffect(() => {
		if (!showModal) return;
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') closeModal();
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [showModal, closeModal]);

	if (!showModal) return null;

	return (
		<div
			className={styles.overlay}
			style={overlayStyle}
			onClick={closeModal}
			aria-label={t('comped-ticket-cancel-invite-modal-overlay-label', 'Dismiss dialog')}
		>
			<div
				className={styles.modal}
				style={containerStyle}
				onClick={(e) => e.stopPropagation()}
				role="dialog"
				aria-modal="true"
			>
				<h2 className={styles.header}>{modalInfo.modalTitle}</h2>
				<div className={styles.containerBody}>
					<Icon iconName="trashGrey" className={styles.icon} />
					<p className={styles.text}>{modalInfo.modalMessage}</p>
				</div>
				<div className={styles.containerActionButtons}>
					<PunchupButton
						variant="outline"
						onClick={closeModal}
						text={t('comped-ticket-cancel-invite-modal-dismiss-btn')}
						type="button"
					/>
					<PunchupButton
						variant="primary"
						onClick={modalInfo.onDelete}
						text={t('comped-ticket-cancel-invite-modal-cancel-btn')}
						type="button"
					/>
				</div>
			</div>
		</div>
	);
};
